import React, { useState, useEffect } from 'react';
import { useForm, FormProvider, SubmitHandler, Control } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from 'components/ui';
import { OnboardingData } from './types';
import {
  saveOnboardingData,
  fetchOnboardingData,
  OnboardingDataWithCompletion,
} from 'lib/supabase-db';
import { STEP_CONFIGS, TOTAL_STEPS } from './constants';
import { StepNavigation } from './components/StepNavigation';
import { supabase } from 'config/supabase';
import { useAuth } from 'contexts/AuthContext';
import { useUI } from 'contexts/UIContext';

import { Step0Gateway } from './steps/Step0Gateway';
import { Step1ProductFeatures } from './steps/Step1ProductFeatures';
import { Step2BrandIdentity } from './steps/Step2BrandIdentity';
import { Step3CompetitiveLandscape } from './steps/Step3CompetitiveLandscape';
import { Step4TargetMarket } from './steps/Step4TargetMarket';
import { Step5BusinessGoals } from './steps/Step5BusinessGoals';
import { Step6OnlinePresence } from './steps/Step6OnlinePresence';
import { Step7BrandAssets } from './steps/Step7BrandAssets';
import { Step8Summary } from './steps/Step8Summary';
import CirculerShadow from 'components/ui/CirculerShadow';
import { PageHeader } from 'components/layout/PageHeader';

const getCurrentStep = () => {
  const params = new URLSearchParams(window.location.search);
  const step = params.get('step');

  if (step) {
    const stepNum = parseInt(step);
    if (!isNaN(stepNum) && stepNum >= 0 && stepNum <= 8) {
      return stepNum;
    }
  }

  // Default to Step 0 (gateway)
  return 0;
};

const Onboarding: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(getCurrentStep());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useUI();

  useEffect(() => {
    window.history.replaceState({}, '', '/onboarding');
  }, []);

  // Listen for URL changes and update current step
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const step = params.get('step');

    if (step) {
      const stepNum = parseInt(step);
      if (!isNaN(stepNum) && stepNum >= 0 && stepNum <= 8) {
        setCurrentStep(stepNum);
      }
    } else {
      setCurrentStep(0);
    }
  }, [location.search]); // Re-run when URL search params change

  // Check if user has completed onboarding (has brand materials)
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setIsLoadingOnboarding(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('brand_materials')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        if (error) throw error;
        setHasCompletedOnboarding(data && data.length > 0);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setHasCompletedOnboarding(false);
      } finally {
        setIsLoadingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const methods = useForm<OnboardingData>({
    defaultValues: {
      step1_productFeatures: {
        brandName: '',
        topSellingProduct: '',
        whatPeopleLike: '',
        upcomingDates: '',
        features: '',
        useCases: [],
        targetIndustries: '',
      },
      step2_brandIdentity: {
        businessPurpose: '',
        brandPassion: '',
        brandKeywords: [],
      },
      step3_competitiveLandscape: {
        competitors: [],
        differentiation: '',
      },
      step4_targetMarket: {
        customerPersonas: [],
        customerPersonasDescription: '',
        painPoints: '',
        scale: '',
        areasServed: [],
        hasPhysicalLocation: '',
        physicalAddress: '',
      },
      step5_businessGoals: {
        businessModel: '',
        budget: '',
        ongoingMarketing: '',
        prAgency: '',
        goals1Year: '',
        goals3Years: '',
        goals5Years: '',
      },
      step6_onlinePresence: {
        websiteManager: '',
        brandVoice: '',
        acquisitionChannels: '',
        salesCycle: '',
        uploadedFiles: [],
        teamSize: '',
        contentCapacity: '',
      },
      step7_brandAssets: {
        files: [],
        tone: '',
        wordsToAvoid: [],
        keyThemes: [],
        socialExamples: {
          twitterExamples: [],
          linkedinExamples: [],
          blogUrls: [],
        },
      },
    },
    mode: 'onBlur',
  });

  const { handleSubmit, trigger, reset } = methods;

  // Load saved onboarding data on component mount
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        const savedData = await fetchOnboardingData();
        if (savedData) {
          console.log('Loading saved onboarding data:', savedData);
          // Reset form with saved data, merging with default values to preserve structure
          // Don't validate on load - validation happens on blur
          reset(savedData, {
            keepErrors: false,
            keepIsValid: false,
            keepTouched: false,
            keepDirty: false,
          });
        }
      } catch (error) {
        console.error('Error loading saved onboarding data:', error);
      }
    };

    loadSavedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Enable "Next" button - validation happens when user clicks it
  useEffect(() => {
    setCanGoNext(true);
  }, [currentStep]);

  const onSubmit: SubmitHandler<OnboardingData> = async (data) => {
    // Only handle final submission on step 8
    // Navigation is handled by nextStep() separately
    if (currentStep === TOTAL_STEPS) {
      setIsSubmitting(true);
      try {
        console.log('Saving onboarding data:', data);

        // Add completion metadata
        const completeData: OnboardingDataWithCompletion = {
          ...data,
          isCompleted: true,
          completedAt: new Date().toISOString(),
        };

        // Save to backend and process for RAG
        await saveOnboardingData(completeData);

        console.log('Onboarding data saved successfully');

        // Navigate to dashboard only after successful save
        navigate('/dashboard');
      } catch (error) {
        console.error('Error saving workspace:', error);

        // Extract error message
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === 'object' && error !== null && 'message' in error
            ? String((error as any).message)
            : 'Failed to save workspace. Please try again.';

        // Show error toast instead of alert
        showToast(errorMessage, 'error');

        // Don't navigate to dashboard on error - stay on onboarding page
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const nextStep = async () => {
    const currentConfig = STEP_CONFIGS[currentStep - 1];

    // Validate current step fields before proceeding
    if (currentConfig.validationFields.length > 0) {
      const isValid = await trigger(currentConfig.validationFields as any);

      // If validation fails, errors will be shown on the invalid fields
      if (!isValid) {
        return; // Don't proceed to next step
      }
    }

    // If editing from summary, return to summary after validation
    if (editingFromSummary) {
      setCurrentStep(TOTAL_STEPS); // Go back to Step 8 (Summary)
      setEditingFromSummary(false); // Clear the flag
      return;
    }

    // Proceed to next step if validation passed (normal flow)
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    // If editing from summary, go back to summary instead of previous step
    if (editingFromSummary) {
      setCurrentStep(TOTAL_STEPS); // Go back to Step 8 (Summary)
      setEditingFromSummary(false); // Clear the flag
      return;
    }

    // Normal flow: go to previous step
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepConfig = STEP_CONFIGS[currentStep];

  const handleEditStep = (step: number) => {
    setEditingFromSummary(true); // Mark that we're editing from summary
    setCurrentStep(step);
  };

  // Show loading state while checking onboarding status
  if (isLoadingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading onboarding...</div>
      </div>
    );
  }

  const handleNavigateToStep = (step: number) => {
    setCurrentStep(step);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <Step0Gateway onNavigateToStep={handleNavigateToStep} />;
      case 1:
        return <Step1ProductFeatures control={methods.control as Control<OnboardingData>} />;
      case 2:
        return <Step2BrandIdentity control={methods.control as Control<OnboardingData>} />;
      case 3:
        return <Step3CompetitiveLandscape control={methods.control as Control<OnboardingData>} />;
      case 4:
        return <Step4TargetMarket control={methods.control as Control<OnboardingData>} />;
      case 5:
        return <Step5BusinessGoals control={methods.control as Control<OnboardingData>} />;
      case 6:
        return <Step6OnlinePresence control={methods.control as Control<OnboardingData>} />;
      case 7:
        return <Step7BrandAssets control={methods.control as Control<OnboardingData>} />;
      case 8:
        return (
          <Step8Summary
            data={methods.getValues()}
            onEditStep={handleEditStep}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen overflow-hidden relative ">
      <div className="container z-[999] mx-auto px-4 py-8">
        {/* Header */}
        <PageHeader
          badge={currentStep === 0 ? 'Onboarding' : `Step ${currentStep}`}
          title={currentStepConfig.title}
          subtitle={currentStepConfig.description}
          showBackButton={hasCompletedOnboarding}
          backButtonText="Back to Dashboard"
          backButtonPath="/dashboard/assistant"
        />

        {/* Content */}
        <Card className="max-w-4xl shadow-none border-0 mx-auto">
          <CardContent className="p-8">
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit as SubmitHandler<OnboardingData>)}>
                {renderStepContent()}

                {/* Only show navigation for steps 1-8, not Step 0 */}
                {currentStep > 0 && (
                  <StepNavigation
                    currentStep={currentStep}
                    totalSteps={TOTAL_STEPS}
                    onNext={nextStep}
                    onPrev={prevStep}
                    isSubmitting={isSubmitting}
                    canGoNext={canGoNext}
                    editingFromSummary={editingFromSummary}
                  />
                )}
              </form>
            </FormProvider>
          </CardContent>
        </Card>
      </div>

      {/* <div className='absolute -z-50 bottom-0 left-3'> */}
      <CirculerShadow className="bg-purple-300/20 w-96 h-96 absolute -top-40 -right-20" />
      <CirculerShadow className="absolute w-96 h-96 -bottom-40 left-0" />
      {/* </div> */}
    </div>
  );
};

export { Onboarding };
